import React, { useEffect, useMemo, useRef, useState } from "react";
import { Col, Form, Grid, Row, notification } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { MemberProfile } from "../../../../model/members/MemberProfile";
import { HealthAnswer } from "../../../../model/members/HealthFormSummary";
import { QuestionnaireSchema } from "../../forms/QuestionnaireSchema";
import { schemaToAnswers } from "../../forms/schemaToAnswers";
import DynamicQuestionnaire from "../../forms/DynamicQuestionnaire";
import DirectDebitInput from "../DirectDebitInput";
import { phoneNumberRule } from "../../../../utils/phone";
import { Alert } from "../../../../ui/primitives/Alert";
import { Button } from "../../../../ui/primitives/Button";
import { DatePicker } from "../../../../ui/primitives/DatePicker";
import { Input } from "../../../../ui/primitives/Input";
import { Modal } from "../../../../ui/primitives/Modal";
import { Stack } from "../../../../ui/primitives/Stack";
import { Steps } from "../../../../ui/primitives/Steps";
import { Text } from "../../../../ui/primitives/Text";
import { useTokens } from "../../../../core/theme/TokensProvider";
import { MemberProfileMode } from "./modeConfig";
import {
    CompletionSection,
    DirectDebitMandateInput,
    RequiredPersonalField,
    hasRealDateOfBirth,
    isDirectDebitComplete,
    isPersonalFieldFilled,
} from "./completion";

// What the wizard collected, handed to the screen to persist. Only the keys for
// the sections the wizard actually walked are present.
export interface CompleteProfilePayload {
    personal?: Partial<MemberProfile>;
    directDebit?: DirectDebitMandateInput & { cancellationNoticeAgreed?: boolean };
    healthAnswers?: HealthAnswer[];
}

interface CompleteProfileWizardProps {
    open: boolean;
    // The outstanding sections, in order. Drives which steps exist — a member
    // missing only their health questionnaire gets a one-section wizard.
    sections: CompletionSection[];
    member: MemberProfile;
    mode: MemberProfileMode;
    healthSchema: QuestionnaireSchema;
    // Client-specific direct-debit cancellation terms (empty => none configured).
    cancellationNotice: string;
    onClose: () => void;
    // Fired on each closed→open transition, so the screen can reset per-run state
    // (its ledger of which sections already saved).
    onOpened?: () => void;
    // Persists everything in one go. Throwing keeps the wizard open so the member
    // doesn't lose what they typed.
    onSave: (payload: CompleteProfilePayload) => Promise<void>;
}

interface PersonalFormValues {
    name: string;
    phoneNumber: string;
    dateOfBirth: Dayjs | undefined;
    addressLine1: string;
    addressLine2: string;
    townCity: string;
    countyState: string;
    postZipCode: string;
    emergencyContactName: string;
    emergencyContactPhone: string;
    emergencyContactEmail: string;
}

// Placeholder text ("To be Completed" — what guest signup and legacy imports wrote
// to satisfy NOT NULL columns) must land as an EMPTY input, not as a pre-filled
// answer. Otherwise the member clicks straight through, saves the placeholder back
// unchanged, and the banner reports the same field again — an unclearable loop.
// Same treatment DirectDebitInput gives the mandate fields.
const realOrBlank = (field: RequiredPersonalField["name"], value: string | undefined): string =>
    isPersonalFieldFilled(field, value) ? (value ?? "") : "";

const buildPersonalInitialValues = (member: MemberProfile): PersonalFormValues => {
    const emergency = member.metadata?.EmergencyContact;
    return {
        name: realOrBlank("name", member.name),
        phoneNumber: realOrBlank("phoneNumber", member.phoneNumber),
        // A sentinel date-of-birth (see completion.ts) must land as an empty picker so
        // the member is actually prompted for it rather than shown "01 Jan 0001".
        dateOfBirth: hasRealDateOfBirth(member.dateOfBirth) ? dayjs(member.dateOfBirth) : undefined,
        addressLine1: realOrBlank("addressLine1", member.addressLine1),
        addressLine2: member.addressLine2 ?? "",
        townCity: realOrBlank("townCity", member.townCity),
        countyState: member.countyState ?? "",
        postZipCode: realOrBlank("postZipCode", member.postZipCode),
        emergencyContactName: emergency?.name ?? "",
        emergencyContactPhone: emergency?.phoneNumber ?? "",
        emergencyContactEmail: emergency?.emailAddress ?? "",
    };
};

// Guided completion of an incomplete member profile. Steps are built from the
// outstanding sections only, so a member is never walked through something they've
// already done, and a final review step carries the single "Complete Profile"
// action that saves the lot.
//
// Within one open session, every step body stays mounted (inactive ones are hidden
// with CSS) so moving back and forth never loses typed values and the direct-debit
// panel isn't re-created underneath the member.
//
// The Modal primitive destroys its children on close, but the two Form instances
// live on THIS component (which isn't destroyed) and rc-field-form keeps its store
// across a remount — `preserve` defaults to true, so `initialValues` would lose to
// last session's typed values. Hence the explicit resetFields() on each open. It is
// keyed on `open` alone on purpose: an effect that also depended on `member` would
// re-fire mid-edit when the late photo fetch replaces the member object and wipe
// what the member had typed (the same trap PersonalDetailsForm documents).
const CompleteProfileWizard: React.FC<CompleteProfileWizardProps> = ({
    open,
    sections,
    member,
    mode,
    healthSchema,
    cancellationNotice,
    onClose,
    onOpened,
    onSave,
}) => {
    const tokens = useTokens();
    const screens = Grid.useBreakpoint();
    const isNarrow = screens.md === false;
    const [personalForm] = Form.useForm<PersonalFormValues>();
    const [healthForm] = Form.useForm();
    const [current, setCurrent] = useState(0);
    const [saving, setSaving] = useState(false);
    const [directDebit, setDirectDebit] = useState<DirectDebitMandateInput | null>(null);
    const [directDebitAgreed, setDirectDebitAgreed] = useState(false);
    // Held in a ref so the open effect can notify the parent without taking the
    // callback as a dependency (which would re-fire the reset on every parent render).
    const onOpenedRef = useRef(onOpened);
    onOpenedRef.current = onOpened;

    const needsPersonal = sections.some((s) => s.key === "personal");
    const needsDirectDebit = sections.some((s) => s.key === "directDebit");
    const needsHealth = sections.some((s) => s.key === "health");

    // A mandate is only accepted once all four fields are valid; the club's
    // cancellation terms (when configured) must also be ticked.
    const hasCancellationTerms = cancellationNotice.trim().length > 0;
    const directDebitReady = isDirectDebitComplete(directDebit) && (!hasCancellationTerms || directDebitAgreed);

    // Restart at step 1 on each open, reseed the forms from the member's CURRENT
    // saved state, and drop any mandate lifted from a previous session — the panel's
    // own fields were destroyed with the closed modal, so keeping the values would
    // leave the Next gate satisfied over empty inputs.
    // Deliberately keyed on `open` alone (see the component comment).
    useEffect(() => {
        if (!open) return;
        setCurrent(0);
        setDirectDebit(null);
        setDirectDebitAgreed(false);
        personalForm.resetFields();
        healthForm.resetFields();
        onOpenedRef.current?.();
    }, [open, personalForm, healthForm]);

    // Step order matches `sections`, with a review step appended last.
    const steps = useMemo(
        () => [...sections.map((section) => ({ key: section.key, title: section.title })), { key: "review" as const, title: "Review & finish" }],
        [sections],
    );

    // Clamp the step if `sections` shrinks while the wizard is open (possible when
    // a background refresh resolves a section as complete) — an out-of-range
    // `current` would render a modal with no step body at all.
    useEffect(() => {
        setCurrent((step) => Math.min(step, steps.length - 1));
    }, [steps.length]);

    const activeKey = steps[current]?.key;
    const isReviewStep = activeKey === "review";

    // Validate whatever the current step collects before letting the member move on.
    // Returns false when the step isn't complete (AntD has already surfaced the
    // inline field errors, or we've explained the direct-debit gate).
    const validateStep = async (key: string | undefined): Promise<boolean> => {
        if (key === "personal") {
            try {
                await personalForm.validateFields();
                return true;
            } catch {
                return false;
            }
        }
        if (key === "directDebit") {
            if (directDebitReady) return true;
            notification.warning({
                message: "Direct debit incomplete",
                description:
                    hasCancellationTerms && isDirectDebitComplete(directDebit)
                        ? "Please accept the cancellation terms to continue."
                        : "Please enter your account holder name, bank, 6-digit sort code and 8-digit account number.",
            });
            return false;
        }
        if (key === "health") {
            try {
                await healthForm.validateFields();
            } catch {
                notification.warning({
                    message: "Health questionnaire incomplete",
                    description: "Please answer the required health questions before continuing.",
                });
                return false;
            }
            // A schema with no required fields validates while entirely blank, which
            // would serialise to zero answers — submitting nothing, then telling the
            // member their profile is complete while the banner reappears.
            if (schemaToAnswers(healthSchema, healthForm.getFieldsValue()).length === 0) {
                notification.warning({
                    message: "Health questionnaire incomplete",
                    description: "Please answer the health questions before continuing.",
                });
                return false;
            }
            return true;
        }
        return true;
    };

    const handleNext = async () => {
        if (!(await validateStep(activeKey))) return;
        setCurrent((step) => Math.min(step + 1, steps.length - 1));
    };

    const handleBack = () => setCurrent((step) => Math.max(step - 1, 0));

    // Final action. Re-validates every collected step and hands the whole payload
    // to the screen.
    const handleComplete = async () => {
        for (const section of sections) {
            if (!(await validateStep(section.key))) {
                // Land the member back on the offending step so the errors are visible.
                setCurrent(sections.findIndex((s) => s.key === section.key));
                return;
            }
        }

        const payload: CompleteProfilePayload = {};

        if (needsPersonal) {
            const values = personalForm.getFieldsValue();
            payload.personal = {
                name: values.name,
                phoneNumber: values.phoneNumber,
                dateOfBirth: values.dateOfBirth ? new Date(values.dateOfBirth.format("YYYY-MM-DD")) : undefined,
                addressLine1: values.addressLine1,
                addressLine2: values.addressLine2,
                townCity: values.townCity,
                countyState: values.countyState,
                postZipCode: values.postZipCode,
                // Merged onto the member's existing metadata by the screen — never
                // replacing it, or a save here would drop GuestType / LeadSource / Notes.
                // Explicit null when cleared rather than an omitted key: the repository
                // only touches keys that are present, and deletes the row on a null
                // value, so omitting it would make an existing contact unremovable.
                metadata: {
                    EmergencyContact: values.emergencyContactName?.trim()
                        ? {
                              name: values.emergencyContactName.trim(),
                              phoneNumber: values.emergencyContactPhone?.trim() || null,
                              emailAddress: values.emergencyContactEmail?.trim() || null,
                          }
                        : null,
                },
            };
        }

        if (needsDirectDebit && directDebit) {
            payload.directDebit = {
                ...directDebit,
                // Only record acceptance when terms were actually presented.
                cancellationNoticeAgreed: hasCancellationTerms ? directDebitAgreed : undefined,
            };
        }

        if (needsHealth) {
            payload.healthAnswers = schemaToAnswers(healthSchema, healthForm.getFieldsValue());
        }

        setSaving(true);
        try {
            await onSave(payload);
            onClose();
        } catch {
            // The screen has already surfaced the failure; keep the wizard open with
            // everything the member typed still in place.
        } finally {
            setSaving(false);
        }
    };

    // Inactive steps are hidden rather than unmounted — see the component comment.
    const stepBody = (key: string, children: React.ReactNode) => (
        <div style={{ display: activeKey === key ? "block" : "none" }} data-testid={`complete-profile-step-${key}`}>
            {children}
        </div>
    );

    return (
        <Modal
            open={open}
            title="Complete your profile"
            onClose={onClose}
            maskClosable={false}
            width={760}
            data-testid="complete-profile-wizard"
            footer={
                <Stack direction="horizontal" gap={2} justify="flex-end">
                    <Button variant="default" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    {current > 0 && (
                        <Button variant="default" onClick={handleBack} disabled={saving} data-testid="complete-profile-back">
                            Back
                        </Button>
                    )}
                    {isReviewStep ? (
                        <Button variant="primary" onClick={handleComplete} loading={saving} data-testid="complete-profile-submit">
                            Complete Profile
                        </Button>
                    ) : (
                        <Button variant="primary" onClick={handleNext} disabled={saving} data-testid="complete-profile-next">
                            Next
                        </Button>
                    )}
                </Stack>
            }
        >
            <Steps
                current={current}
                size="small"
                items={steps.map((step) => ({ title: step.title }))}
                // Stack the labels on a phone so a three-step wizard doesn't
                // overflow the modal.
                direction={isNarrow ? "vertical" : "horizontal"}
                style={{ marginBottom: tokens.space[5] }}
            />

            {needsPersonal &&
                stepBody(
                    "personal",
                    // The profile behind this modal renders its own copy of these fields,
                    // so the form is named: AntD prefixes the input ids with it, keeping
                    // them unique and each label bound to its own input.
                    <Form<PersonalFormValues>
                        form={personalForm}
                        name="completeProfilePersonal"
                        layout="vertical"
                        requiredMark
                        size="middle"
                        initialValues={buildPersonalInitialValues(member)}
                    >
                        <Row gutter={14}>
                            <Col xs={24}>
                                <Form.Item
                                    name="name"
                                    label="Full name"
                                    // Self-service locks the name (staff own it). Only require it
                                    // when the member can actually change it — otherwise a blank
                                    // locked name would be an unfixable gate. `computeProfileCompletion`
                                    // drops it from the outstanding list on the same condition.
                                    rules={mode.editName ? [{ required: true, message: "Please enter your full name" }] : []}
                                    style={{ marginBottom: tokens.space[3] }}
                                >
                                    <Input disabled={!mode.editName} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={14}>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="phoneNumber"
                                    label="Phone number"
                                    rules={[{ required: true, message: "Please enter your phone number" }, phoneNumberRule()]}
                                    style={{ marginBottom: tokens.space[3] }}
                                >
                                    <Input type="tel" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="dateOfBirth"
                                    label="Date of birth"
                                    rules={[{ required: true, message: "Please enter your date of birth" }]}
                                    style={{ marginBottom: tokens.space[3] }}
                                >
                                    <DatePicker format="DD MMM YYYY" allowClear={false} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={14}>
                            <Col xs={24}>
                                <Form.Item
                                    name="addressLine1"
                                    label="Address line 1"
                                    rules={[{ required: true, message: "Please enter your address" }]}
                                    style={{ marginBottom: tokens.space[3] }}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={14}>
                            <Col xs={24}>
                                <Form.Item name="addressLine2" label="Address line 2" style={{ marginBottom: tokens.space[3] }}>
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={14}>
                            <Col xs={24} sm={8}>
                                <Form.Item
                                    name="townCity"
                                    label="Town / City"
                                    rules={[{ required: true, message: "Please enter your town or city" }]}
                                    style={{ marginBottom: 0 }}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col xs={12} sm={8}>
                                <Form.Item name="countyState" label="County" style={{ marginBottom: 0 }}>
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col xs={12} sm={8}>
                                <Form.Item
                                    name="postZipCode"
                                    label="Postcode"
                                    rules={[{ required: true, message: "Please enter your postcode" }]}
                                    style={{ marginBottom: 0 }}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>
                        {/* Prompted, but never `required` — the completion banner asks
                            for an emergency contact while the Save gate deliberately
                            doesn't block on it (see completion.ts hasEmergencyContact). */}
                        <Text weight="medium" style={{ display: "block", marginTop: tokens.space[4] }}>
                            Emergency contact
                        </Text>
                        <Row gutter={14}>
                            <Col xs={24} sm={8}>
                                {/* The name is what carries the contact — a phone or
                                    email with nobody attached is dropped on save, so
                                    demand a name once either is filled in. */}
                                <Form.Item
                                    name="emergencyContactName"
                                    label="Name"
                                    dependencies={["emergencyContactPhone", "emergencyContactEmail"]}
                                    rules={[
                                        ({ getFieldValue }) => ({
                                            validator: (_, value) => {
                                                const anyContact =
                                                    !!getFieldValue("emergencyContactPhone")?.trim() ||
                                                    !!getFieldValue("emergencyContactEmail")?.trim();
                                                if (!anyContact || value?.trim()) return Promise.resolve();
                                                return Promise.reject(
                                                    new Error("Add a name for your emergency contact"),
                                                );
                                            },
                                        }),
                                    ]}
                                    style={{ marginBottom: 0 }}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col xs={12} sm={8}>
                                {/* A name with no way to reach them doesn't clear the
                                    prompt (hasEmergencyContact needs both), so require a
                                    phone once a name is given — unless an email is. */}
                                <Form.Item
                                    name="emergencyContactPhone"
                                    label="Phone"
                                    dependencies={["emergencyContactName", "emergencyContactEmail"]}
                                    rules={[
                                        ({ getFieldValue }) => ({
                                            validator: (_, value) => {
                                                const named = !!getFieldValue("emergencyContactName")?.trim();
                                                const hasEmail = !!getFieldValue("emergencyContactEmail")?.trim();
                                                if (!named || hasEmail || value?.trim()) return Promise.resolve();
                                                return Promise.reject(
                                                    new Error("Add a phone or an email for your emergency contact"),
                                                );
                                            },
                                        }),
                                    ]}
                                    style={{ marginBottom: 0 }}
                                >
                                    <Input type="tel" inputMode="tel" />
                                </Form.Item>
                            </Col>
                            <Col xs={12} sm={8}>
                                <Form.Item
                                    name="emergencyContactEmail"
                                    label="Email"
                                    rules={[{ type: "email", message: "Please enter a valid email address" }]}
                                    style={{ marginBottom: 0 }}
                                >
                                    <Input type="email" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>,
                )}

            {needsDirectDebit &&
                stepBody(
                    "directDebit",
                    <DirectDebitInput
                        membershipId={member.membershipId}
                        selfServiceMode
                        canEdit
                        // No mandate is on file (that's why this step exists), so skip the
                        // fetch and lift the entered values up — they're saved by the
                        // wizard's Complete Profile action, not the panel's own button.
                        isCreating
                        onCreateChange={setDirectDebit}
                        mandateRequired
                        cancellationNotice={cancellationNotice}
                        onAgreedChange={setDirectDebitAgreed}
                        formName="completeProfileDirectDebit"
                    />,
                )}

            {needsHealth &&
                stepBody(
                    "health",
                    <>
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: tokens.space[4] }}
                            message="Please answer the health questions below so the club knows how to look after you safely."
                        />
                        <DynamicQuestionnaire schema={healthSchema} form={healthForm} formName="completeProfileHealth" />
                    </>,
                )}

            {stepBody(
                "review",
                <Stack gap={3}>
                    <Text>You've filled in everything we need. Submit to finish setting up your profile.</Text>
                    <Stack gap={2}>
                        {sections.map((section) => (
                            <span key={section.key} data-testid={`complete-profile-review-${section.key}`}>
                                {/* Token colour, not the `--ft-success` CSS variable: the modal
                                    renders in a portal on document.body, outside the
                                    `.ft-profile-v2` subtree that variable is scoped to. */}
                                <CheckCircleOutlined style={{ color: tokens.color.success, marginRight: tokens.space[2] }} />
                                {section.title}
                            </span>
                        ))}
                    </Stack>
                </Stack>,
            )}
        </Modal>
    );
};

export default CompleteProfileWizard;
